<template>
  <div>
    <v-dialog
      v-model="dialogOpen"
      v-if="dialogOpen"
      content-class="my-custom-dialog"
      persistent
      scrollable
      max-width="60%"
    >
      <v-card tile color="white">
        <v-card-title>
          <h2 class="text--secondary">
            {{ $t("$vuetify.cashRegister.search") }}
          </h2>
          <v-spacer></v-spacer>
          <v-icon large @click="close">mdi-close</v-icon>
        </v-card-title>
        <v-card-text class="white">
          <v-progress-linear v-if="!loaded" indeterminate></v-progress-linear>
          <v-container class="panel-container">
            <!-- Search Bar + Quantity Dropdown Row -->
            <v-row class="panel-row">
              <v-col v-if="quantityBarEnabled" cols="12" md="2">
                <quantity-dropdown
                  :selected-quantity="dialogSelectedQuantity"
                  :enabled="true"
                  @quantity-changed="onDialogQuantityChanged"
                ></quantity-dropdown>
              </v-col>
              <v-col :cols="quantityBarEnabled ? '12' : '12'" :md="quantityBarEnabled ? '10' : '12'">
                <v-text-field
                  autofocus
                  ref="defInput"
                  v-model="searchText"
                  label="Pretraži artikle po nazivu ili šifri"
                  data-layout="normalAduro"
                  append-icon="mdi-magnify"
                  clearable
                  outlined
                  rounded
                  dense
                  hide-details
                  class="search-text-field"
                  @click="showKbd; selectSearchText(); focusedRowIndex = -1"
                  @focus="focusedIndex = -1; focusedRowIndex = -1; selectSearchText()"
                  @click:append="searchProducts()"
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- Tiles Display (Original) -->
            <v-row v-if="displayMode === 'tiles'" class="panel-row">
              <v-col
                v-for="(item, idxc) in cols"
                :key="idxc"
                :cols="4"
                class="panel-cell"
                align="center"
              >
                <v-btn
                  style="border-radius: 12px; display: inline-block"
                  block
                  tile
                  depressed
                  min-height="6rem"
                  :color="item.color"
                  :class="{ 'focused-item': focusedIndex === idxc }"
                  return-object
                  @click="onCellClick(item)"
                >
                  <span class="white--text tile">{{ item.label }}</span>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Rows Display (New) -->
            <div v-if="displayMode === 'rows'" class="rows-container">
              <!-- Product Rows -->
              <v-list v-if="cols.length > 0" class="product-list">
                <template v-for="(item, idx) in cols">
                  <v-list-item
                    :key="idx"
                    :ref="`row-${idx}`"
                    tabindex="0"
                    @click="onRowClick(item, idx)"
                    @focus="focusedRowIndex = idx"
                    :class="['product-row', { 'focused': focusedRowIndex === idx }]"
                    :ripple="true"
                  >
                    <v-list-item-content class="py-2">
                      <v-row align="center" no-gutters>
                        <!-- Naziv (Name) -->
                        <v-col cols="4" class="text-left">
                          <div class="product-name text-subtitle-1 font-weight-medium">
                            {{ item.label }}
                          </div>
                        </v-col>

                        <!-- Šifra (Code) -->
                        <v-col cols="1" class="text-center">
                          <div class="product-code text-body-2 grey--text">
                            {{ item.code || '-' }}
                          </div>
                        </v-col>

                        <!-- Cijena (Price) -->
                        <v-col cols="2" class="text-right">
                          <div class="product-price text-subtitle-1 font-weight-bold primary--text">
                            {{ formatPrice(item.price) }}
                          </div>
                        </v-col>

                        <!-- Quantity Badge -->
                        <v-col cols="1" class="text-right">
                          <v-chip
                            v-if="itemQuantities[item.id] > 0"
                            small
                            color="success"
                            text-color="white"
                            class="quantity-badge"
                          >
                            {{ itemQuantities[item.id] }}
                          </v-chip>
                        </v-col>

                        <!-- Kol Input -->
                        <v-col cols="1" class="text-center px-1 pl-3" @click.stop @keydown.stop>
                          <v-text-field
                            :ref="`qtyInput-${idx}`"
                            v-model.number="quantityInputs[item.id]"
                            type="number"
                            min="1"
                            dense
                            outlined
                            hide-details
                            class="qty-input"
                            placeholder="Kol"
                            @click.stop="focusedRowIndex = idx"
                            @focus="focusedRowIndex = idx"
                            @keydown.enter.stop.prevent="setQuantity(item, $event)"
                          ></v-text-field>
                        </v-col>

                        <!-- Mjerna jedinica (Unit) -->
                        <v-col cols="1" class="text-right">
                          <div class="product-unit text-body-2 grey--text">
                            {{ item.unit || 'kom' }}
                          </div>
                        </v-col>

                        <!-- Actions Column -->
                        <v-col cols="2" class="text-left">
                          <div class="action-icons">
                            <!-- Plus icon - always visible -->
                            <v-btn
                              icon
                              small
                              color="success"
                              class="action-btn"
                              aria-label="Dodaj artikl"
                              @click.stop="onIconClick(item, 'add', idx)"
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>

                            <!-- Minus icon - only if qty > 0 -->
                            <v-btn
                              v-if="itemQuantities[item.id] > 0"
                              icon
                              small
                              color="warning"
                              class="action-btn"
                              aria-label="Umanji količinu"
                              @click.stop="onIconClick(item, 'decrement', idx)"
                            >
                              <v-icon>mdi-minus</v-icon>
                            </v-btn>

                            <!-- Trash icon - only if qty > 0 -->
                            <v-btn
                              v-if="itemQuantities[item.id] > 0"
                              icon
                              small
                              color="error"
                              class="action-btn"
                              aria-label="Ukloni artikl"
                              @click.stop="onIconClick(item, 'removeAll', idx)"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider v-if="idx < cols.length - 1" :key="`divider-${idx}`"></v-divider>
                </template>
              </v-list>
            </div>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-container
      class="kbd"
      v-if="!$vuetify.breakpoint.smAndDown"
      ref="keyboard"
      v-show="oskVisible"
      :style="{ 'max-width': width }"
    >
      <div class="kbd-header"></div>
      <vue-touch-keyboard
        :options="oskOptions"
        :layout="layout"
        :cancel="hideKbd"
        :accept="accept"
        :input="input"
      />
    </v-container>
  </div>
</template>

<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import { clone } from '@/plugins/utils'
import onScreenKeyboard from '@/mixins/onScreenKeyboard'
import { collection, query, getDocs, where, limit } from 'firebase/firestore'
import triGram from '@/mixins/trigram'
import QuantityDropdown from '../register-wide/QuantityDropdown.vue'

export default {
  mixins: [onScreenKeyboard, triGram],
  components: {
    QuantityDropdown
  },
  props: {
    closeModalOnProductClick: {
      type: Boolean,
      default: false
    },
    keyboardShortcutsEnabled: {
      type: Boolean,
      default: false
    },
    displayMode: {
      type: String,
      default: 'tiles',
      validator: (val) => ['tiles', 'rows'].includes(val)
    },
    itemQuantities: {
      type: Object,
      default: () => ({})
    },
    quantityBarEnabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialogOpen: true,
      products: [],
      cols: [],
      searchText: '',
      priceListItems: [],
      loaded: true,
      documents: [],
      items: [],
      menuItems: [],
      currency: 'EUR',
      focusedIndex: -1, // For tiles mode (Tab navigation)
      focusedRowIndex: -1, // For rows mode (Arrow navigation)
      quantityInputs: {}, // Track quantity inputs for each item
      // Dialog-specific quantity management
      dialogSelectedQuantity: 1
    }
  },
  watch: {
    searchText (nv) {
      if (!nv || nv === null || nv === '') {
        this.priceListItems = []
        this.cols = []
        this.focusedIndex = -1
        this.focusedRowIndex = -1
        this.$forceUpdate()
      }
    }
  },
  methods: {
    selectSearchText () {
      this.$nextTick(() => {
        if (this.$refs.defInput && this.$refs.defInput.$refs.input) {
          this.$refs.defInput.$refs.input.select()
        }
      })
    },

    handleKeyNavigation (event) {
      // Handle plus key for increment in row mode (only if shortcuts enabled)
      if (event.key === '+' && this.keyboardShortcutsEnabled && this.displayMode === 'rows' && this.focusedRowIndex >= 0) {
        event.preventDefault()
        const item = this.cols[this.focusedRowIndex]
        if (item) {
          this.onIconClick(item, 'add')
        }
        return
      }

      // Handle minus key for decrement in row mode (only if shortcuts enabled)
      if (event.key === '-' && this.keyboardShortcutsEnabled && this.displayMode === 'rows' && this.focusedRowIndex >= 0) {
        event.preventDefault()
        const item = this.cols[this.focusedRowIndex]
        if (item && this.itemQuantities[item.id] > 0) {
          this.onIconClick(item, 'decrement')
        }
        return
      }

      // Handle X key for remove all in row mode (only if shortcuts enabled)
      if ((event.key === 'x' || event.key === 'X') && this.keyboardShortcutsEnabled && this.displayMode === 'rows' && this.focusedRowIndex >= 0) {
        event.preventDefault()
        const item = this.cols[this.focusedRowIndex]
        if (item && this.itemQuantities[item.id] > 0) {
          this.onIconClick(item, 'removeAll')
        }
        return
      }

      if (event.key === 'Escape') {
        // Check if we're NOT in an input field, or if we're navigating products
        const inInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)

        // If navigating products (not in search box), first ESC should focus and select search
        if (!inInputField || this.focusedIndex >= 0 || this.focusedRowIndex >= 0) {
          // First ESC: Focus and select search input
          event.preventDefault()
          event.stopPropagation()
          this.focusedIndex = -1
          this.focusedRowIndex = -1
          this.focusAndSelectSearchInput()
          return
        }

        // Second ESC (already in search input): Close modal
        event.preventDefault()
        this.close()
        return
      }

      // Row mode navigation with Arrow keys
      if (this.displayMode === 'rows' && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault()
        if (event.key === 'ArrowDown') {
          this.moveDown()
        } else if (event.key === 'ArrowUp') {
          this.moveUp()
        }
        return
      }

      // Tiles mode navigation with Tab
      if (event.key === 'Tab' && this.cols.length > 0 && this.displayMode === 'tiles') {
        event.preventDefault()
        // Blur the search input when starting keyboard navigation
        if (this.$refs.defInput) {
          this.$refs.defInput.blur()
        }
        if (event.shiftKey) {
          // Shift+Tab - go backwards
          this.focusedIndex = this.focusedIndex <= 0 ? this.cols.length - 1 : this.focusedIndex - 1
        } else {
          // Tab - go forwards
          this.focusedIndex = this.focusedIndex >= this.cols.length - 1 ? 0 : this.focusedIndex + 1
        }
      } else if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()

        if (this.displayMode === 'rows' && this.focusedRowIndex >= 0 && this.cols.length > 0) {
          // Row mode - add focused row item
          const item = this.cols[this.focusedRowIndex]
          if (item) {
            this.onCellClick(item)
          }
        } else if (this.displayMode === 'tiles' && this.focusedIndex >= 0 && this.cols.length > 0) {
          // Tiles mode - add focused tile item
          const item = this.cols[this.focusedIndex]
          if (item) {
            this.onCellClick(item)
          }
        } else {
          // Search if no item is focused
          this.searchProducts()
        }
      }
    },

    moveDown () {
      const maxIndex = this.cols.length - 1
      if (this.focusedRowIndex === -1 && maxIndex >= 0) {
        // If nothing focused, focus first item
        this.focusedRowIndex = 0
        this.scrollToFocusedRow()
        this.focusRow()
      } else if (this.focusedRowIndex < maxIndex) {
        this.focusedRowIndex++
        this.scrollToFocusedRow()
        this.focusRow()
      }
    },

    moveUp () {
      if (this.focusedRowIndex > 0) {
        this.focusedRowIndex--
        this.scrollToFocusedRow()
        this.focusRow()
      } else if (this.focusedRowIndex === 0) {
        // If at first item, move focus back to search
        this.focusedRowIndex = -1
        if (this.$refs.defInput) {
          this.$refs.defInput.focus()
        }
      }
    },

    focusRow () {
      // Focus the entire row element (v-list-item)
      this.$nextTick(() => {
        const refName = `row-${this.focusedRowIndex}`
        const rowRef = this.$refs[refName]
        if (rowRef && rowRef[0]) {
          rowRef[0].$el.focus()
        }
      })
    },

    scrollToFocusedRow () {
      // Wait for DOM to update and render
      this.$nextTick(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            // The actual scrollable container is .rows-container (has overflow-y: auto)
            const container = document.querySelector('.rows-container')
            if (!container) return

            const focusedElement = document.querySelector('.product-row.focused')
            if (!focusedElement) return

            // Use getBoundingClientRect for reliable positioning
            const containerRect = container.getBoundingClientRect()
            const elementRect = focusedElement.getBoundingClientRect()

            // Calculate relative position of element to container
            const relativeTop = elementRect.top - containerRect.top
            const relativeBottom = elementRect.bottom - containerRect.top

            // Check if element is above visible area
            if (relativeTop < 0) {
              container.scrollTop += relativeTop
            }
            // Check if element is below visible area
            else if (relativeBottom > containerRect.height) {
              container.scrollTop += (relativeBottom - containerRect.height)
            }
          }, 50)
        })
      })
    },

    focusAndSelectSearchInput () {
      this.$nextTick(() => {
        if (this.$refs.defInput) {
          this.$refs.defInput.focus()
          // Select all text in the input
          if (this.$refs.defInput.$refs.input) {
            this.$refs.defInput.$refs.input.select()
          }
        }
      })
    },

    formatPrice (price) {
      if (!price) return '-'
      // Price is in cents, divide by 100
      const amount = price / 100
      // Use currency symbol instead of code
      const symbol = this.currency === 'EUR' ? '€' : (this.currency === 'HRK' ? 'kn' : this.currency)
      return `${amount.toFixed(2)} ${symbol}`
    },

    onRowClick (item, idx) {
      // Set focused row when clicking on the row
      this.focusedRowIndex = idx
      // Call the original onCellClick logic
      this.onCellClick(item)
    },

    onCellClick (i) {
      const item = this.cols.find(it => it.id === i.id)
      this.$emit('addProduct', item, this.dialogSelectedQuantity)
      // Auto-reset quantity after adding
      this.dialogSelectedQuantity = 1
      // Close modal only if setting is enabled
      if (this.closeModalOnProductClick) {
        this.$emit('closeSearchItemsDialog')
      }
    },

    // Dialog quantity handlers
    onDialogQuantityChanged (quantity) {
      this.dialogSelectedQuantity = quantity
    },

    onIconClick (item, operation, idx) {
      // Set focused row when clicking on an icon
      if (idx !== undefined) {
        this.focusedRowIndex = idx
      }
      // Emit event to parent with item and operation
      this.$emit('modifyProduct', item, operation)
      // Note: Never close modal for icon actions
    },

    setQuantity (item, event) {
      const qty = this.quantityInputs[item.id]

      if (!qty || qty < 1) {
        // Clear invalid input
        this.$set(this.quantityInputs, item.id, null)
        return
      }

      // Emit event with item and quantity
      this.$emit('setProductQuantity', item, qty)

      // Clear input after setting
      this.$nextTick(() => {
        this.$set(this.quantityInputs, item.id, null)
      })

      // Blur (remove focus from) the input field
      if (event && event.target) {
        event.target.blur()
      }
    },

    close () {
      // Clear all quantity inputs when closing
      this.quantityInputs = {}
      this.dialogSelectedQuantity = 1 // Reset dialog quantity
      this.$emit('closeSearchItemsDialog')
    },

    async searchProducts () {
      this.loaded = false
      this.priceListItems = []
      this.cols = []
      this.focusedIndex = -1
      this.focusedRowIndex = -1

      if (!this.searchText || this.searchText === '' || this.searchText === null) {
        this.$forceUpdate()
        this.loaded = true
        return
      }

      var trigramObject = this.triGram(this.searchText)
      var items = await this.getItems(trigramObject, 'items')
      var menuItems = await this.getItems(trigramObject, 'menu')

      if (menuItems && menuItems.length > 0) {
        items.push(...menuItems)
      }

      if (items && items.length > 0) {
        items.forEach(product => {
          if (product.warehouse_units && product.warehouse_units.units) {
            Object.keys(product.warehouse_units.units).forEach(key => {
              if (product.warehouse_units.units[key].prices && product.warehouse_units.units[key].prices[this.currency].active === true) {
                let newProduct = {}
                newProduct = clone(product)
                newProduct.prices = product.warehouse_units.units[key].prices

                var stUnit = product.warehouse_units.units[key].storage_unit
                if (stUnit === '.kom') {
                  stUnit = ''
                }
                if (product.warehouse_units.units[key].base_unit === true) {
                  stUnit = product.selling_unit
                }
                newProduct.name = product.name + ' ' + stUnit
                newProduct.id = newProduct.id + '.' + product.warehouse_units.units[key].id
                this.cols.push({
                  type: newProduct.type,
                  color: '#2C5A90',
                  id: newProduct.id,
                  label: newProduct.name,
                  code: product.code || '',
                  price: newProduct.prices?.[this.currency]?.price || 0,
                  unit: stUnit || 'kom'
                })
              }
            })
          } else {
            this.cols.push({
              type: product.type,
              color: '#2C5A90',
              id: product.id,
              label: product.name,
              code: product.code || '',
              price: product.prices?.[this.currency]?.price || 0,
              unit: product.selling_unit || 'kom'
            })
          }
        })
      }

      // Sort results: exact code match first, then others
      const searchTextLower = this.searchText.toLowerCase().trim()
      this.cols.sort((a, b) => {
        const aCodeLower = (a.code || '').toLowerCase().trim()
        const bCodeLower = (b.code || '').toLowerCase().trim()

        const aExactMatch = aCodeLower === searchTextLower
        const bExactMatch = bCodeLower === searchTextLower

        if (aExactMatch && !bExactMatch) return -1
        if (!aExactMatch && bExactMatch) return 1
        return 0
      })

      this.loaded = true

      // Auto-focus first row in rows mode if there are results
      if (this.displayMode === 'rows' && this.cols.length > 0) {
        this.$nextTick(() => {
          this.focusedRowIndex = 0
          this.scrollToFocusedRow()
        })
      }
    },

    async getItems (triGramObject = undefined, type = 'items') {
      var searchCollection = 'location_pricelist'
      if (type === 'menu') {
        searchCollection = 'location_menu_pricelist'
      }
      const searchConstraints = []
      if (triGramObject) {
        Object.keys(triGramObject).forEach(name =>
          searchConstraints.push(where(`meta.${name}`, '==', true))
        )
      }

      searchConstraints.push(limit(20))

      searchConstraints.push(where('status', '==', 'OK'))
      searchConstraints.push(where('active', '==', true))

      const constraints = [
        collection(df, `${searchCollection}/${state.getPointOfSale().id}/items`),
        ...searchConstraints
      ]

      const q = query.apply(this, constraints)

      const querySnapshot = await getDocs(q)

      const results = []
      querySnapshot.forEach(doc => {
        var item = doc.data()
        if (type === 'menu') {
          item.type = 'menu-product'
        }
        results.push({ _id: doc.id, ...item })
      }
      )

      return results
    }
  },
  mounted () {
    this.currency = 'HRK'
    if (Date.now() / 1000 > 1672527599) {
      this.currency = 'EUR'
    }

    this.setEnterAction(this.searchProducts)
    this.setPosition(this.$refs.keyboard, '217px', '502px')
    const ref = this.$refs.defInput.$refs.input
    this.showKbdOnMount(ref, 'normalAduro')

    // Add global keydown listener for Tab and Enter navigation
    document.addEventListener('keydown', this.handleKeyNavigation)
  },

  beforeDestroy () {
    // Remove global keydown listener
    document.removeEventListener('keydown', this.handleKeyNavigation)
  }
}
</script>

<style scoped>
.v-dialog__content >>> .my-custom-dialog {
  position: absolute;
  top: 50px;
  border-radius: 25px;
}
.panel-container {
  margin-top: 1rem;
}
.text-wrap {
  width: min-content;
  margin: auto;
}
.panel-cell {
  padding: 0 1px 1px 0;
}
.active {
  background-color: var(--v-accent-darken3);
  color: white;
}
.label {
  font-size: 0.6rem;
  color: white;
}
span.tile {
  display: block;
  white-space: normal;
  max-width: 330px;
  font-weight: 700;
  font-size: 1.4rem;
}
.focused-item {
  border: 3px solid #FFD700 !important;
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.5) !important;
}

/* Row Display Styles */
.rows-container {
  max-height: 60vh;
  overflow-y: auto;
}

.product-list {
  background: transparent;
}

.product-row {
  cursor: pointer;
  transition: background-color 0.2s, border-left 0.2s;
  border-radius: 8px;
  border-left: 3px solid transparent;
}

.product-row:hover {
  background-color: rgba(33, 150, 243, 0.08);
}

.product-row.focused {
  background-color: rgba(33, 150, 243, 0.15);
  border-left: 3px solid #2196F3;
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2);
}

.product-name {
  color: #333;
  line-height: 1.4;
}

.product-code {
  font-family: monospace;
  font-size: 0.875rem;
}

.product-price {
  color: #2196F3;
}

.product-unit {
  text-transform: uppercase;
  font-size: 0.8rem;
}

.quantity-badge {
  font-weight: bold;
  min-width: 32px;
  justify-content: center;
}

.action-icons {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  align-items: center;
  padding: 4px 0 4px 12px;
  min-width: 144px;
}

.action-btn {
  min-width: 40px;
  min-height: 40px;
  border: 2px solid currentColor;
  border-radius: 4px;
}

.action-btn.v-btn--icon {
  border-radius: 4px !important;
}

/* Qty Input Field */
.qty-input >>> input {
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.qty-input >>> .v-input__control {
  min-height: 36px !important;
}

.qty-input >>> fieldset {
  border-color: #2196F3 !important;
  border-width: 2px !important;
}

.qty-input >>> input:focus {
  background-color: rgba(33, 150, 243, 0.05);
}

/* Hide number input arrows */
.qty-input >>> input[type="number"]::-webkit-inner-spin-button,
.qty-input >>> input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Search Text Field Styling */
.search-text-field >>> .v-input__control {
  min-height: 3.5rem !important;
  height: 3.5rem !important;
}

.search-text-field >>> .v-input__slot {
  min-height: 3.5rem !important;
  height: 3.5rem !important;
}

.search-text-field >>> fieldset {
  border-width: 2px !important;
  border-color: rgba(52, 87, 120, 0.4) !important;
  border-radius: 28px !important;
}

.search-text-field >>> .v-text-field__slot {
  height: 3.5rem !important;
  display: flex !important;
  align-items: center !important;
}

.search-text-field:hover >>> fieldset {
  border-color: rgba(52, 87, 120, 0.6) !important;
}

.search-text-field >>> .v-input--is-focused fieldset {
  border-color: #345778 !important;
  border-width: 2px !important;
}
</style>
